/*
 * Custom - bottom-pinned product info column (desktop only)
 * =========================================================
 * The info column is position:sticky, pinned --pp-sticky-top from the top
 * of the viewport. That leaves empty space between it and the bottom of the
 * browser window. This nudges it down so it sits --pp-pin-bottom from the
 * bottom edge instead.
 *
 * Why JS is needed: CSS cannot express "natural at rest, bottom-aligned once
 * stuck". A sticky `bottom` offset only ever shifts an element upward, so it
 * never engages for an element sitting at the top of its container; and a
 * large `top` offset would shove the column down the page at rest, away from
 * the top of the product image.
 *
 * The nudge is a transform, so it never interferes with the browser's own
 * sticky maths - it is applied on top of wherever sticky has already put the
 * element. It is clamped so the column can never travel past the bottom of
 * its container, which keeps it level with the end of the description.
 */
(function () {
	'use strict';

	var MIN_WIDTH = 990;
	var EASE_MS = 220;
	var items = [];

	var reduceMotion = window.matchMedia
		? window.matchMedia('(prefers-reduced-motion: reduce)')
		: { matches: false };

	function pxVar(el, name, fallback) {
		var raw = getComputedStyle(el).getPropertyValue(name).trim();
		if (!raw) return fallback;
		// Values are authored in rem; resolve against the root font size.
		if (raw.indexOf('rem') > -1) {
			return parseFloat(raw) * parseFloat(getComputedStyle(document.documentElement).fontSize);
		}
		return parseFloat(raw) || fallback;
	}

	function apply(item, offset) {
		var el = item.el;

		if (offset === item.offset) return;

		// Ease only when the column first leaves its resting position, and let
		// that ease finish. Writing transition:'none' on the next scroll frame
		// - as an earlier version did - cancels it mid-flight and the column
		// snaps across instead of sliding.
		var now = Date.now();
		if (item.offset === 0 && offset > 0 && !reduceMotion.matches) {
			item.easeUntil = now + EASE_MS;
		}
		var easing = now < item.easeUntil;

		el.style.transition = easing ? 'transform ' + EASE_MS + 'ms ease-out' : 'none';
		el.style.transform = offset > 0 ? 'translateY(' + offset + 'px)' : '';
		item.offset = offset;
	}

	function update() {
		items.forEach(function (item) {
			var el = item.el;
			var wrap = item.wrap;

			if (window.innerWidth < MIN_WIDTH) {
				el.classList.remove('pp-unstick');
				apply(item, 0);
				return;
			}

			var gap = pxVar(el, '--pp-pin-bottom', 20);

			// If the column cannot fit between the sticky offset and the bottom
			// of the window, sticking it would strand its lower part - possibly
			// the add-to-cart button - off screen. Let it scroll normally.
			el.classList.remove('pp-unstick');
			var stickyTop = parseFloat(getComputedStyle(el).top) || 0;
			if (el.offsetHeight + stickyTop + gap > window.innerHeight) {
				apply(item, 0);
				el.classList.add('pp-unstick');
				return;
			}

			// No downward nudge. An earlier version pushed the column down so it
			// sat just above the fold, but the column starts level with the top
			// of the product image, so getting to the bottom of the window means
			// travelling DOWN the screen while the reader scrolls DOWN the page.
			// That fights the scroll and reads as the column being yanked
			// downwards - roughly 170px on a hardtop, 280px on a single-image
			// product. Easing it only slowed the lurch down.
			//
			// Plain top-pinned sticky is smooth, which is what matters most
			// while someone is reading. A compact bar fixed to the bottom of the
			// window is the pattern to reach for if a bottom-anchored buy button
			// is wanted - it is a separate element, so it never has to move.
			apply(item, 0);
		});
	}

	var ticking = false;
	function schedule() {
		if (ticking) return;
		ticking = true;
		window.requestAnimationFrame(function () {
			update();
			ticking = false;
		});
	}

	function collect() {
		if (items.ro) items.ro.disconnect();
		var ro = window.ResizeObserver ? new ResizeObserver(schedule) : null;

		items = Array.prototype.slice.call(
			document.querySelectorAll('.product-page:not(.product-section-featured) .product-info')
		).filter(function (el) { return el.parentElement; })
		 .map(function (el) {
			// Height changes without a scroll event - a variant swap loading a
			// different-aspect image, a late image load, a tab opening - would
			// otherwise leave a stale offset behind.
			if (ro) { ro.observe(el); ro.observe(el.parentElement); }
			return { el: el, wrap: el.parentElement, offset: 0, easeUntil: 0 };
		});
		items.ro = ro;
	}

	function init() {
		collect();
		if (!items.length) return;
		update();
		window.addEventListener('scroll', schedule, { passive: true });
		window.addEventListener('resize', schedule, { passive: true });
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}

	// The theme editor re-renders sections without a page load.
	document.addEventListener('shopify:section:load', function () {
		collect();
		update();
	});
})();
